import { Plus, Save, Trash2 } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Badge, Card, InlineMessage, PageHeader, SectionHeading } from "../components/ui";
import { useWorkspace } from "../lib/workspace";
import type { BusinessContext, Faq, Product } from "../../shared/types";

export function BusinessContextPage() {
  const { data } = useWorkspace();
  return data ? <ContextEditor key={data.context.version} initialContext={data.context} /> : null;
}

function ContextEditor({ initialContext }: { initialContext: BusinessContext }) {
  const { data, saveContext } = useWorkspace();
  const [draft, setDraft] = useState<BusinessContext>(structuredClone(initialContext));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<"profile" | "products" | "faqs" | "voice">("profile");

  if (!data || !draft) return null;

  async function handleSave(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setSaveError(null);
    try {
      await saveContext({ ...draft, rules: draft.rules.map(s => s.trim()).filter(Boolean), faqs: draft.faqs.map(f => ({...f, variants: f.variants.map(s => s.trim()).filter(Boolean)})) });
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Save failed. Your edits are still here.');
    } finally {
      setSaving(false);
    }
  }

  function addProduct() {
    const product: Product = {
      id: crypto.randomUUID(),
      name: "",
      description: "",
      priceText: "",
      currency: "MMK",
      active: true,
    };
    setDraft((current) => current ? { ...current, products: [...current.products, product] } : current);
  }

  function updateProduct(id: string, patch: Partial<Product>) {
    setDraft((current) => current ? {
      ...current,
      products: current.products.map((product) => product.id === id ? { ...product, ...patch } : product),
    } : current);
  }

  function addFaq() {
    const faq: Faq = {
      id: crypto.randomUUID(),
      productId: null,
      question: "",
      answer: "",
      variants: [],
      approved: false,
      sensitive: false,
      active: true,
    };
    setDraft((current) => current ? { ...current, faqs: [...current.faqs, faq] } : current);
  }

  function updateFaq(id: string, patch: Partial<Faq>) {
    setDraft((current) => current ? {
      ...current,
      faqs: current.faqs.map((faq) => faq.id === id ? { ...faq, ...patch } : faq),
    } : current);
  }

  return (
    <form onSubmit={(event) => void handleSave(event)}>
      <PageHeader
        eyebrow={`Context version ${draft.version}`}
        title="Business Context"
        description="The approved facts, voice and boundaries AutoSocial may use. Incoming messages can never edit this source."
        actions={<button className="button button--primary" type="submit" disabled={saving}><Save size={17} />{saving ? "Saving…" : "Save context"}</button>}
      />

      <InlineMessage tone="info" title="Approvals follow the context version">
        Changing these facts invalidates any unused approval created with an older version.
      </InlineMessage>
      {saveError ? <InlineMessage tone="danger" title="Context not saved">{saveError}</InlineMessage> : null}

      <div className="context-layout">
        <Card className="context-tabs-card">
          <div className="context-tabs" role="tablist" aria-label="Business context sections">
            {([
              ["profile", "Profile & CTAs"],
              ["products", `Products (${draft.products.length})`],
              ["faqs", `FAQs (${draft.faqs.length})`],
              ["voice", "Voice & rules"],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={activeTab === value}
                className={activeTab === value ? "active" : ""}
                onClick={() => setActiveTab(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </Card>

        <Card className="context-editor">
          {activeTab === "profile" ? (
            <div className="form-section">
              <SectionHeading title="Business profile" description="Describe who DrTun serves and what this workspace represents." />
              <label className="field">
                <span>Profile</span>
                <textarea aria-label="Profile" aria-describedby="profile-help" rows={7} value={draft.profile} onChange={(event) => setDraft({ ...draft, profile: event.target.value })} placeholder="Approved business description…" />
                <small id="profile-help">Only approved factual claims belong here.</small>
              </label>
              <SectionHeading title="Calls to action" description="Reusable next steps for drafts and replies." action={<button className="button button--ghost button--small" type="button" onClick={() => setDraft({ ...draft, ctas: [...draft.ctas, { label: "", value: "" }] })}><Plus size={15} />Add CTA</button>} />
              <div className="stack-list">
                {draft.ctas.map((cta, index) => (
                  <div className="inline-fields" key={index}>
                    <label className="field"><span>Label</span><input value={cta.label} onChange={(event) => setDraft({ ...draft, ctas: draft.ctas.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item) })} /></label>
                    <label className="field field--grow"><span>Approved wording</span><input value={cta.value} onChange={(event) => setDraft({ ...draft, ctas: draft.ctas.map((item, itemIndex) => itemIndex === index ? { ...item, value: event.target.value } : item) })} /></label>
                    <button className="icon-button icon-button--danger field-action" type="button" onClick={() => setDraft({ ...draft, ctas: draft.ctas.filter((_, itemIndex) => itemIndex !== index) })} aria-label="Remove CTA"><Trash2 size={17} /></button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {activeTab === "products" ? (
            <div className="form-section">
              <SectionHeading title="Products and prices" description="Channels can choose from these approved facts." action={<button className="button button--secondary button--small" type="button" onClick={addProduct}><Plus size={15} />Add product</button>} />
              <div className="editor-list">
                {draft.products.map((product) => (
                  <div className="editor-item" key={product.id}>
                    <div className="editor-item__heading">
                      <input className="title-input" value={product.name} onChange={(event) => updateProduct(product.id, { name: event.target.value })} placeholder="Product name" />
                      <label className="compact-check"><input type="checkbox" checked={product.active} onChange={(event) => updateProduct(product.id, { active: event.target.checked })} />Active</label>
                      <button className="icon-button icon-button--danger" type="button" onClick={() => setDraft({ ...draft, products: draft.products.filter((item) => item.id !== product.id) })} aria-label="Delete product"><Trash2 size={17} /></button>
                    </div>
                    <label className="field"><span>Description</span><textarea rows={3} value={product.description} onChange={(event) => updateProduct(product.id, { description: event.target.value })} /></label>
                    <div className="inline-fields">
                      <label className="field field--grow"><span>Approved price wording</span><input value={product.priceText} onChange={(event) => updateProduct(product.id, { priceText: event.target.value })} placeholder="Example: 50,000 MMK or Ask for current price" /></label>
                      <label className="field field--short"><span>Currency</span><select value={product.currency} onChange={(event) => updateProduct(product.id, { currency: event.target.value })}><option>MMK</option><option>USD</option><option>THB</option></select></label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {activeTab === "faqs" ? (
            <div className="form-section">
              <SectionHeading title="FAQ library" description="Auto-reply requires an approved exact variant and is still off per channel by default." action={<button className="button button--secondary button--small" type="button" onClick={addFaq}><Plus size={15} />Add FAQ</button>} />
              <div className="editor-list">
                {draft.faqs.map((faq) => (
                  <div className="editor-item" key={faq.id}>
                    <div className="editor-item__heading"><strong>{faq.question || "New FAQ"}</strong><Badge tone={faq.sensitive ? "red" : faq.approved ? "green" : "amber"}>{faq.sensitive ? "Review only" : faq.approved ? "Approved" : "Draft"}</Badge><button className="icon-button icon-button--danger" type="button" onClick={() => setDraft({ ...draft, faqs: draft.faqs.filter((item) => item.id !== faq.id) })} aria-label="Delete FAQ"><Trash2 size={17} /></button></div>
                    <label className="field"><span>Canonical question</span><input lang="my" value={faq.question} onChange={(event) => updateFaq(faq.id, { question: event.target.value })} /></label>
                    <label className="field"><span>Approved answer</span><textarea lang="my" rows={4} value={faq.answer} onChange={(event) => updateFaq(faq.id, { answer: event.target.value })} /></label>
                    <label className="field"><span>Exact question variants — one per line</span><textarea lang="my" rows={3} value={faq.variants.join("\n")} onChange={(event) => updateFaq(faq.id, { variants: event.target.value.split("\n") })} /></label>
                    <div className="check-row">
                      <label className="compact-check"><input type="checkbox" checked={faq.approved} onChange={(event) => updateFaq(faq.id, { approved: event.target.checked })} />Approved for exact-match use</label>
                      <label className="compact-check"><input type="checkbox" checked={faq.sensitive} onChange={(event) => updateFaq(faq.id, { sensitive: event.target.checked, approved: event.target.checked ? false : faq.approved })} />Sensitive — always review</label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {activeTab === "voice" ? (
            <div className="form-section">
              <SectionHeading title="Voice" description="Describe how drafts should sound in English and Burmese." />
              <label className="field"><span>Voice guide</span><textarea rows={7} value={draft.voice} onChange={(event) => setDraft({ ...draft, voice: event.target.value })} /></label>
              <SectionHeading title="Business rules" description="One immutable instruction per line. Incoming text has lower priority." />
              <label className="field"><span>Rules</span><textarea rows={10} value={draft.rules.join("\n")} onChange={(event) => setDraft({ ...draft, rules: event.target.value.split("\n") })} /></label>
            </div>
          ) : null}
        </Card>
      </div>

      <div className="sticky-save">
        <span>Version {draft.version} · Saving creates a new context version</span>
        <button className="button button--primary" type="submit" disabled={saving}><Save size={17} />{saving ? "Saving…" : "Save context"}</button>
      </div>
    </form>
  );
}
